import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TbCurrencyDram } from 'react-icons/tb';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addToCart } from '../../features/cart/cartSlice';
import Modal from '../Modal/Modal';
import styles from './compareModal.module.css';

type Row = { field: string; labelKey: string };

// Характеристики по категориям (как на странице товара). Бренд+номер — всегда.
const CATEGORY_ROWS: Record<string, Row[]> = {
    spark_plugs: [
        { field: 'key_type', labelKey: 'key_type' },
        { field: 'key_size', labelKey: 'key_size' },
        { field: 'seat_type', labelKey: 'seat_type' },
        { field: 'thread_size', labelKey: 'thread_size' },
        { field: 'thread_length', labelKey: 'thread_length' },
        { field: 'gap', labelKey: 'gap' },
        { field: 'electrodes_number', labelKey: 'electrodes_number' },
        { field: 'electrode_type', labelKey: 'electrode_type' },
    ],
    ignition_coils: [
        { field: 'plugs_number', labelKey: 'plugs_number' },
        { field: 'contacts_number', labelKey: 'contacts_number' },
    ],
    ignition_coil_mouthpieces: [
        { field: 'wired', labelKey: 'wired' },
        { field: 'type_', labelKey: 'type' },
        { field: 'contact_type', labelKey: 'contact_type' },
    ],
    airbag_cables: [
        { field: 'steering_axle_bore_diameter', labelKey: 'steering_axle_bore_diameter' },
        { field: 'airbag_plugs_number', labelKey: 'airbag_cable_plugs_number' },
    ],
    crankshaft_sensors: [
        { field: 'wired', labelKey: 'wired' },
        { field: 'contact_number', labelKey: 'contacts_number' },
        { field: 'connection_type', labelKey: 'connection_type' },
    ],
    camshaft_sensors: [
        { field: 'wired', labelKey: 'wired' },
        { field: 'contact_number', labelKey: 'contacts_number' },
        { field: 'connection_type', labelKey: 'connection_type' },
    ],
};

interface Props {
    open: boolean;
    onClose: () => void;
}

const CompareModal: FC<Props> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { products, compareCategory } = useAppSelector((s) => s.compare);

    const rows: Row[] = [
        { field: 'brand', labelKey: 'brand' },
        { field: 'detail_number', labelKey: 'detail_number' },
        ...(CATEGORY_ROWS[compareCategory] || []),
    ];

    const val = (p: any, field: string) => {
        const v = p?.[field];
        return v === null || v === undefined || v === '' ? '—' : String(v);
    };

    // Строка подсвечивается, если у товаров разные значения
    const isDiff = (field: string) => new Set(products.map((p) => val(p, field))).size > 1;

    const handleAdd = (product: any) => {
        dispatch(addToCart({ count_original: 1, count_copy: 0, product, product_id: product.product_id }));
    };

    return (
        <Modal open={open} onClose={onClose}>
            <div className={styles.wrap}>
                {products.length === 0 ? (
                    <p className={styles.empty}>—</p>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.char_head}>{t('product_features.characteristic')}</th>
                                    {products.map((p) => (
                                        <th key={p.product_id} className={styles.prod_head}>
                                            <div className={styles.prod_title}>{p.model || p.detail_number}</div>
                                            <div className={styles.prod_price}>
                                                {Number(p.price_original || p.price_copy || 0).toLocaleString('ru-RU')}{' '}
                                                <TbCurrencyDram />
                                            </div>
                                            <button className={styles.cart_btn} onClick={() => handleAdd(p)}>
                                                {t('general.add_to_cart')}
                                            </button>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.field} className={isDiff(row.field) ? styles.diff : ''}>
                                        <td className={styles.label}>{t(`product_features.${row.labelKey}`)}</td>
                                        {products.map((p) => (
                                            <td key={p.product_id} className={styles.value}>{val(p, row.field)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className={styles.note}>
                            <span className={styles.note_box} />
                            {t('product_features.compare_diff_note')}
                        </p>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default CompareModal;
